import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { questionnaireApiService } from "../services/questionnaireApiService";
import { onboardingApiService } from "@/features/onboarding/services/onboardingApiService";
import type {
  FinancialNeedOption,
  DesiredAmountOption,
} from "../types/questionnaire";
import type { UserOnboardingState } from "@/features/onboarding/types/onboarding";

interface UseQuestionnaireReturn {
  isQuestionnaireCompleted: boolean;
  isSavingFinancialNeed: boolean;
  isSavingDesiredAmount: boolean;
  saveFinancialNeed: (
    financialNeed: FinancialNeedOption,
  ) => Promise<UserOnboardingState>;
  saveDesiredAmount: (
    desiredAmount: DesiredAmountOption,
  ) => Promise<UserOnboardingState>;
}

export const useQuestionnaire = (
  fetchOnboardingState = false,
): UseQuestionnaireReturn => {
  const queryClient = useQueryClient();

  const onboardingStateQuery = useQuery({
    queryKey: ["onboarding-state"],
    queryFn: () => onboardingApiService.getOnboardingStatus(),
    enabled: fetchOnboardingState,
  });

  const isQuestionnaireCompleted = Boolean(
    (onboardingStateQuery.data?.financialNeed &&
      onboardingStateQuery.data?.desiredAmount) ||
      (onboardingStateQuery.data?.userChoice &&
        onboardingStateQuery.data?.ficoScoreReason),
  );

  const onSuccess = (onboardingState: UserOnboardingState) => {
    queryClient.setQueryData(["onboarding-state"], onboardingState);
  };

  const saveFinancialNeedMutation = useMutation<
    UserOnboardingState,
    Error,
    FinancialNeedOption
  >({
    mutationFn: (financialNeed) =>
      questionnaireApiService.updateIntroState({
        userChoice: "credit",
        financialNeed,
      }),
    onSuccess,
  });

  const saveDesiredAmountMutation = useMutation<
    UserOnboardingState,
    Error,
    DesiredAmountOption
  >({
    mutationFn: (desiredAmount) =>
      questionnaireApiService.updateIntroState({ desiredAmount }),
    onSuccess,
  });

  return {
    isQuestionnaireCompleted,
    isSavingFinancialNeed: saveFinancialNeedMutation.isPending,
    isSavingDesiredAmount: saveDesiredAmountMutation.isPending,
    saveFinancialNeed: saveFinancialNeedMutation.mutateAsync,
    saveDesiredAmount: saveDesiredAmountMutation.mutateAsync,
  };
};
